import { HonestWork } from '@cian/honestwork-component';
import { OwnerBadge } from '@cian/owner-badge-component';
import cn from 'classnames';
import * as React from 'react';

import {
  IAgentSchema as IAgentSchemaOld,
  IOfferSchema as IOfferSchemaOld,
} from 'shared/types/api-models/offer-card/v1/get-offer-data';

import { Tag } from '../../../../common/components/tag';
import { IAgentSchema } from '../../../../repositories/offer-card/entities/agent/AgentSchema';
import { IUserLevelInfoSchema } from '../../../../repositories/offer-card/entities/agent/UserLevelInfoSchema';
import { IOfferSchema } from '../../../../repositories/offer-card/entities/offer/OfferSchema';
import { getVerifyWay, isHomeownerVerified, getAgentName } from '../../../../utils/agent';
import { CommercialOwner } from '../../../../utils/agent/CommercialOwner';
import { createAgentIdName } from '../../../../utils/createAgentIdName';
import { CommercialOwnerTag } from '../../../CommercialOwnerTag';
import { PartnerTooltip } from '../../../PartnerTooltip';
import { TagsWrapper } from '../../../TagsWrapper';
import { UserWorkHonestlyAsideInfo } from '../../../UserWorkHonestlyAsideInfo';
import { getCardType, trackGoToCard } from '../../tracking';
import * as styles from '../AuthorAsideBrand/index.css';

export interface IHonestAsideProps {
  isHonestAgent: boolean;
  isProAgent: boolean;
  isCianPartner?: boolean | null;
  label?: string;
  agent: IAgentSchemaOld;
  offer: IOfferSchemaOld;
  agentLevelInfo?: IUserLevelInfoSchema | null;
  profileLink?: string;
}

export const HonestAsideBrand = ({
  isHonestAgent,
  isProAgent,
  isCianPartner,
  label,
  agent,
  offer,
  agentLevelInfo,
  profileLink,
}: IHonestAsideProps) => {
  const isVerified = React.useMemo(() => isHomeownerVerified(offer, agent), [offer, agent]);
  const verifyWay = React.useMemo(() => getVerifyWay(offer, agent), [offer, agent]);
  const agentId = React.useMemo(() => createAgentIdName(agent), [agent]);
  const handleAgentNameClick = React.useCallback(
    () => trackGoToCard(getCardType(offer), 'name', 'floating_block'),
    [offer],
  );
  const commercialOwner = React.useMemo(
    () => CommercialOwner.getInstanceIfIsCommercial(offer as IOfferSchema, agent as IAgentSchema),
    [offer, agent],
  );
  const nameHandler = profileLink ? handleAgentNameClick : undefined;
  const name = React.useMemo(() => (agent.isSubAgent ? agent.companyName : getAgentName(agent, offer)), [offer, agent]);

  if (
    ![isHonestAgent, isProAgent, isCianPartner, label, commercialOwner?.shield.showOwner, agentLevelInfo].some(Boolean)
  ) {
    return null;
  }

  return (
    <TagsWrapper>
      {agentLevelInfo && (
        <div className={styles['tag']}>
          <UserWorkHonestlyAsideInfo
            agentLevelInfo={agentLevelInfo}
            nameHandler={nameHandler}
            agentName={agent.bank || name || agentId}
            agent={agent}
            isAgentPublished={!!profileLink}
            profileLink={profileLink}
            isProAgent={isProAgent}
          />
        </div>
      )}
      {!agentLevelInfo && isHonestAgent && (
        <HonestWork
          agentLink={{
            agentId: agent.id,
            otherLinkProps: {
              deal_type: offer.dealType,
              offer_type: offer.offerType,
            },
          }}
          className={styles['tag']}
        />
      )}
      {!agentLevelInfo && isProAgent && (
        <Tag className={cn(styles['tag'], styles['tag-pro'])} title="Профессиональный пользователь">
          Pro
        </Tag>
      )}
      {commercialOwner?.shield.showOwner && <CommercialOwnerTag type={commercialOwner.shield.tagType} />}
      {!!label &&
        (label === 'homeowner' ? (
          <OwnerBadge isVerified={isVerified} verifyWay={verifyWay} />
        ) : (
          <Tag className={styles['tag']}>{label}</Tag>
        ))}
      {isCianPartner && (
        <PartnerTooltip master={<Tag className={cn(styles['tag'], styles['tag-partner'])}>Партнёр</Tag>} />
      )}
    </TagsWrapper>
  );
};
